import axios from "axios";
import i18next from "i18next";
import {redirectToLogin} from "../helpers/redirectToLogin";
import {clearSessionScopedStorage} from "../helpers/sessionScopedStorage";
import {EFFECTIVE_FACILITY_HEADER, recordEffectiveFacility} from "./effectiveFacility";
import { getSurveyResponseToken, requiresSurveyResponseToken, SURVEY_RESPONSE_TOKEN_HEADER } from "./publicTokens";

export const API_PERMISSION_DENIED_EVENT = 'permission-denied';
export const API_VALIDATION_ERROR_EVENT = 'api-validation-error';
/** 403 `empty_scope` : le périmètre de l'utilisateur ne contient plus aucun hôtel. */
export const API_EMPTY_SCOPE_EVENT = 'api-empty-scope';

/**
 * L'échec réseau OBSERVÉ, et son retour — #24, #86.
 *
 * Émis depuis l'intercepteur plutôt que déduit de `navigator.onLine`, qui ment
 * précisément dans le cas hôtelier : Wi-Fi capté sans lien montant, il renvoie
 * `true`. Voir `mobile/hooks/useReseau.ts`, seul consommateur pour l'instant.
 */
export const NETWORK_FAILURE_EVENT = 'tf-echec-reseau';
export const NETWORK_SUCCESS_EVENT = 'tf-succes-reseau';

/**
 * Une erreur sans réponse HTTP : le serveur n'a jamais répondu.
 *
 * C'est la seule forme d'échec qui prouve un problème de LIEN. Un 500 prouve le
 * contraire — la requête est arrivée. `ECONNABORTED` couvre le délai d'abandon.
 */
function estUnEchecReseau(error: { response?: unknown; code?: string }): boolean {
    return !error?.response || error?.code === 'ECONNABORTED';
}

/**
 * Le délai d'abandon par défaut — décision #24, appliqué par #86.
 *
 * Sans lui, une requête suspendue ne renonce **jamais** : sur un Wi-Fi d'hôtel
 * capté mais sans lien montant — sous-sol, ascenseur, portail captif — l'app
 * tourne indéfiniment et l'utilisateur n'apprend rien.
 *
 * 15 s : assez court pour que l'échec soit observable et alimente le bandeau
 * hors ligne, assez long pour ne couper aucune requête métier normale. Le travail
 * réellement long passe déjà par des jobs asynchrones avec sondage (PDF de
 * contrat), donc ses requêtes HTTP sont brèves.
 *
 * Le web en bénéficie au passage : il n'avait aucun délai non plus.
 *
 * ⚠️ **Pas de liste centralisée d'exemptions.** Les appels synchrones vers un LLM
 * (dix à trente secondes) portent leur propre délai **au site d'appel**, là où
 * l'endpoint est nommé — voir `useAiAssistant.ts`. Le motif des `silentFailUrls`
 * de l'intercepteur 403 est écarté sciemment : une liste tenue loin des hooks
 * qu'elle protège finit oubliée, et ce dépôt s'est déjà fait mordre plusieurs
 * fois par des whitelists doubles. Ici l'oubli est bruyant et immédiat — la
 * fonctionnalité qu'on écrit expire sous nos yeux.
 */
export const DELAI_ABANDON_MS = 15_000;

/** Pour les appels synchrones vers un LLM, qui prennent dix à trente secondes. */
export const DELAI_ABANDON_LLM_MS = 60_000;

/**
 * Pour la sauvegarde d'un formulaire, longue par nature : son coût suit le
 * nombre d'éléments. Le plus gros du parc (427 éléments, payload de 1,4 Mo)
 * répond en ~14 s après les correctifs du 05/09/2026 — trop près des 15 s par
 * défaut. Reste sous le `Timeout 60` d'Apache : le front n'abandonne donc
 * jamais avant le serveur, et n'affiche plus d'erreur sur un enregistrement
 * qui a réussi.
 */
export const DELAI_ABANDON_FORMULAIRE_MS = 60_000;

const axiosClient = axios.create({
    baseURL: `${import.meta.env.VITE_API_BASE_URL}/api`,
    timeout: DELAI_ABANDON_MS,
});

/**
 * Détecte une requête partie avec `facility_id=0` (query, params ou corps).
 *
 * `0` n'est jamais un établissement réel : c'est la valeur de repli du pattern
 * `currentFacility?.id ?? 0` quand le contexte n'est pas (encore) chargé. Le
 * backend la refuse en 403 via `TenantScope`, mais ce 403 n'est PAS une perte
 * de droits — le traduire en redirection vers la page d'accueil éjecte
 * l'utilisateur en plein travail (cf. bug mail builder du 02/08/2026).
 */
export function hasZeroFacilityId(config?: {
    url?: string;
    params?: unknown;
    data?: unknown;
}): boolean {
    if (! config) return false;

    // 1. Query string de l'URL
    if (config.url && /[?&]facility_id=0(?:&|$)/.test(config.url)) return true;

    // 2. Params axios
    const params = config.params as Record<string, unknown> | undefined;
    if (params && Number(params.facility_id) === 0 && params.facility_id !== undefined) return true;

    // 3. Corps de requête : FormData, objet, ou JSON déjà sérialisé
    const { data } = config;

    if (typeof FormData !== 'undefined' && data instanceof FormData) {
        const value = data.get('facility_id');
        return value !== null && Number(value) === 0;
    }

    if (typeof data === 'string') {
        try {
            return hasZeroFacilityId({ data: JSON.parse(data) });
        } catch {
            return false;
        }
    }

    if (data && typeof data === 'object') {
        const value = (data as Record<string, unknown>).facility_id;
        return value !== undefined && value !== null && Number(value) === 0;
    }

    return false;
}

axiosClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("ACCESS_TOKEN");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Envoyer le facility sélectionné dans le header pour que le backend puisse filtrer
    const selectedFacilityId = localStorage.getItem("SELECTED_FACILITY_ID");
    if (selectedFacilityId) {
        config.headers["X-Facility-Id"] = selectedFacilityId;
    }

    // Jeton de la réponse de formulaire en cours (routes publiques keyées par id).
    const surveyResponseToken = getSurveyResponseToken();
    if (surveyResponseToken && requiresSurveyResponseToken(config.url)) {
        config.headers[SURVEY_RESPONSE_TOKEN_HEADER] = surveyResponseToken;
    }

    return config;
});

axiosClient.interceptors.response.use(
    (response) => {
        // Une requête qui ABOUTIT est le seul signal de retour du réseau fiable.
        // `navigator.onLine` passe à `true` dès que le Wi-Fi est capté, sans rien
        // dire du lien montant — c'est très exactement le cas hôtelier (#24).
        window.dispatchEvent(new CustomEvent(NETWORK_SUCCESS_EVENT));

        recordEffectiveFacility(response.headers?.[EFFECTIVE_FACILITY_HEADER]);

        return response;
    },
    (error) => {
        const { response } = error;

        // Échec de LIEN — pas de réponse du tout, ou délai d'abandon dépassé.
        // Un 500 ne compte pas : il prouve au contraire que la requête est
        // arrivée. C'est ce signal-là, et pas `navigator.onLine`, qui allume le
        // bandeau hors ligne du mobile.
        if (estUnEchecReseau(error)) {
            window.dispatchEvent(new CustomEvent(NETWORK_FAILURE_EVENT));
        }

        if (response?.status === 401) {
            // Supprimer le token et rediriger vers la page de connexion
            localStorage.removeItem("ACCESS_TOKEN");
            clearSessionScopedStorage();

            // Utiliser une redirection
            redirectToLogin();
        } else if (response?.status === 403) {
            // Liste des URLs pour lesquelles on ne redirige PAS sur 403
            // Ces endpoints sont des requêtes background non-critiques
            const silentFailUrls = [
                '/unread-conversations-count',
                '/ai-usage',
                '/crm/urgent-tasks-count',
                '/changelogs/unread-count',
                '/public-checkin/',
                '/payment-transactions',
                '/payment-methods',
                // page /scan : le refus s'affiche dans la page, pas en redirection
                '/reservation-scan/',
                // 403 métier : le message s'affiche dans l'écran (opt-out refusé par le groupe)
                '/flow-starters-by-group/',
                '/facility-participation',
                '/facility-opt-out',
            ];

            const requestUrl = error.config?.url || '';
            const errorCode = response?.data?.code;
            const isScopeCode = errorCode === 'empty_scope' || errorCode === 'read_only';
            const isSilentUrl = silentFailUrls.some(url => requestUrl.includes(url));

            // Garde-fou : un 403 déclenché par un `facility_id=0` est un bug front,
            // pas une perte de droits. On ne redirige pas, mais on le rend visible.
            const isBogusFacility = hasZeroFacilityId(error.config);
            if (isBogusFacility) {
                console.warn(
                    `[api] 403 sur une requête portant facility_id=0 (${requestUrl}) — ` +
                    `l'établissement courant n'était pas chargé. Pas de redirection.`
                );
            }

            // Codes métier du périmètre : l'un redirige vers son écran, l'autre notifie sans redirection.
            if (errorCode === 'empty_scope') {
                window.dispatchEvent(new CustomEvent(API_EMPTY_SCOPE_EVENT));
            } else if (errorCode === 'read_only') {
                window.dispatchEvent(new CustomEvent(API_VALIDATION_ERROR_EVENT, {
                    detail: { message: i18next.t('global:errors.read_only') }
                }));
            }

            const shouldRedirect = !isSilentUrl && !isBogusFacility && !isScopeCode;

            if (shouldRedirect) {
                window.dispatchEvent(new CustomEvent(API_PERMISSION_DENIED_EVENT, {
                    detail: { url: requestUrl }
                }));
            }
        }

        return Promise.reject(error);
    }
);

export default axiosClient;
